import { Navigate, Route, Routes, useParams } from 'react-router';
import { useSession } from './lib/session.tsx';
import { LoadingRegion } from '@gatecheckpro/design-system';
import { CabsCallbackPage } from './pages/CabsCallbackPage.tsx';
import { SignInPage } from './pages/SignInPage.tsx';
import { RegisterPage } from './pages/RegisterPage.tsx';
import { WorkspaceLayout } from './pages/WorkspaceLayout.tsx';
import { OrganizationsPage } from './pages/OrganizationsPage.tsx';
import { CreateOrganizationPage } from './pages/CreateOrganizationPage.tsx';
import { OverviewPage } from './pages/OverviewPage.tsx';
import { TeamPage } from './pages/TeamPage.tsx';
import { VerificationPage } from './pages/VerificationPage.tsx';
import { ActivityPage } from './pages/ActivityPage.tsx';
import { SecurityPage } from './pages/SecurityPage.tsx';
import { AcceptInvitationPage } from './pages/AcceptInvitationPage.tsx';
import { EventsPage } from './pages/EventsPage.tsx';
import { CreateEventPage } from './pages/CreateEventPage.tsx';
import { VenuesPage } from './pages/VenuesPage.tsx';
import { VenueDetailPage } from './pages/VenueDetailPage.tsx';
import { VenueMapsPage } from './pages/venue-map/VenueMapsPage.tsx';
import { VenueMapDetailPage } from './pages/venue-map/VenueMapDetailPage.tsx';
import { VenueMapEditorPage } from './pages/venue-map/VenueMapEditorPage.tsx';
import { RenderProbe } from './lib/RenderProbe.tsx';
import { EventWorkspace } from './pages/event/EventWorkspace.tsx';
import { EventOverviewPage } from './pages/event/EventOverviewPage.tsx';
import { EventDetailsPage } from './pages/event/EventDetailsPage.tsx';
import { EventSessionsPage } from './pages/event/EventSessionsPage.tsx';
import { EventVenuePage } from './pages/event/EventVenuePage.tsx';
import { EventGatesPage } from './pages/event/EventGatesPage.tsx';
import { EventOperationalAccessPage } from './pages/event/EventOperationalAccessPage.tsx';
import { EventVenueAuthorityPage } from './pages/event/EventVenueAuthorityPage.tsx';
import { EventTicketsPage } from './pages/event/EventTicketsPage.tsx';
import { TicketVersionsPage } from './pages/event/TicketVersionsPage.tsx';
import { SessionSeatingPage } from './pages/seating/SessionSeatingPage.tsx';
import { FinancePage } from './pages/finance/FinancePage.tsx';

export function App() {
  const { state } = useSession();

  if (state.status === 'loading') {
    return (
      <main className="gc-auth">
        <div className="gc-auth__card">
          <LoadingRegion label="Opening your workspace" />
        </div>
      </main>
    );
  }

  if (state.status === 'anonymous') {
    return (
      <Routes>
        <Route path="/sign-in" element={<SignInPage />} />
        <Route path="/register" element={<RegisterPage />} />
        {/*
          Where CABS Identity returns this console's browser. Public, because the person is not
          signed in yet — that is what this page is completing — and on this origin, because the
          session it establishes can only be read here.
        */}
        <Route path="/auth/cabs/callback" element={<CabsCallbackPage />} />
        <Route path="*" element={<Navigate to="/sign-in" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/organizations" element={<OrganizationsPage />} />
      <Route path="/organizations/new" element={<CreateOrganizationPage />} />
      <Route path="/invitations/accept" element={<AcceptInvitationPage />} />
      <Route path="/security" element={<SecurityPage />} />
      <Route path="/o/:organizationId" element={<WorkspaceLayout />}>
        <Route index element={<OverviewPage />} />
        <Route path="events" element={<EventsPage />} />
        <Route path="finance" element={<FinanceRoute />} />
        <Route path="events/new" element={<CreateEventPage />} />
        <Route path="events/:eventId" element={<EventWorkspace />}>
          <Route index element={<EventOverviewPage />} />
          <Route path="details" element={<EventDetailsPage />} />
          <Route path="sessions" element={<EventSessionsPage />} />
          {/*
            Under the session, not under the venue. A venue map is reached from
            the building because it belongs to the building; what may be sold on
            one night belongs to the night, and the URL should say which of the
            two a person is looking at.
          */}
          <Route path="sessions/:sessionId/seating" element={<SessionSeatingPage />} />
          <Route path="venue" element={<EventVenuePage />} />
          <Route path="gates" element={<EventGatesPage />} />
          <Route path="operational-access" element={<EventOperationalAccessPage />} />
          <Route path="venue-authority" element={<EventVenueAuthorityPage />} />
          <Route path="tickets" element={<EventTicketsPage />} />
          <Route path="versions/:ticketProductId" element={<TicketVersionsPage />} />
        </Route>
        <Route path="venues" element={<VenuesPage />} />
        <Route path="venues/:venueId" element={<VenueDetailPage />} />
        <Route path="venues/:venueId/maps" element={<VenueMapsPage />} />
        {/*
          A map is addressed by its own id rather than nested under its venue.
          It is reached from an event's seating configuration as often as from
          the venue it belongs to, and a URL that has to know the venue first
          would make the second route a lie about how the thing is found.
        */}
        <Route path="venue-maps/:venueMapId" element={<VenueMapDetailPage />} />
        {/*
          A version, not a map. Which room a person is drawing is a property of
          the version they opened, and later authority pins exact versions — so
          the URL names the thing that is actually being edited.
        */}
        <Route path="venue-map-versions/:versionId" element={<VenueMapEditorPage />} />
        {/*
          Development only. The boundary that keeps a failed page from blanking
          the workspace has to be shown working against a real render failure,
          and the only honest way to do that in a browser is to have one. A
          production build compiles `import.meta.env.DEV` to false, so this
          route and the module behind it are dropped.
        */}
        {import.meta.env.DEV && <Route path="__render-probe" element={<RenderProbe />} />}
        <Route path="team" element={<TeamPage />} />
        <Route path="verification" element={<VerificationPage />} />
        <Route path="activity" element={<ActivityPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/organizations" replace />} />
    </Routes>
  );
}

/** The finance surface, scoped to the organization in the URL. */
function FinanceRoute(): React.JSX.Element {
  const { organizationId } = useParams<{ organizationId: string }>();
  return <FinancePage organizationId={organizationId ?? ''} />;
}
